import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import {
  RidButton,
  RidCorrectAnswerModal,
  RidMechanicBackground,
  RidMechanicContent,
  RidMechanicWrapper,
  RidModalBackground,
  RidTopBar,
} from '../../components';
import { delay, Waiter } from '@gametailors/web-core/misc';
import { NgClass } from '@angular/common';
import { RidEggCarton } from '../../components/rid-egg-carton/rid-egg-carton';
import { IconBackspace } from '../../icons/i-backspace';
import { IconEnter } from '../../icons/i-enter';
import { AudioEffectPlayer } from '../../utils/audio.utils';
import { RidEggCartonStepAnswer } from './rid-egg-carton-step-answer';
import { PronunciationService } from '../../services/pronunciation.service';

@Component({
  selector: 'rid-egg-carton-sums',
  templateUrl: 'rid-egg-carton-sums.html',
  imports: [
    RidMechanicBackground,
    RidMechanicContent,
    RidMechanicWrapper,
    RidTopBar,
    RidButton,
    NgClass,
    RidEggCarton,
    IconBackspace,
    IconEnter,
    RidEggCartonStepAnswer,
    RidCorrectAnswerModal,
    RidModalBackground,
  ],
  host: {
    class: 'block',
  },
})
export class RidEggCartonSums implements OnInit {
  private readonly $pronunciationService = inject(PronunciationService);

  numbers = input.required<[number, number][]>();
  typedAnswers = input(false);
  numberIndex = signal(0);
  number = computed(() => this.numbers()[this.numberIndex()]);
  answer = computed(() => this.number()[0] + this.number()[1]);
  state = signal<'QUESTION' | 'TENTH' | 'SINGLE' | 'CARRY' | 'WRITE'>('QUESTION');
  cartons = computed(() => Math.floor(this.number()[0] / 10));

  tenths = signal(0);
  singles = signal(0);
  singlesCartonAdded = signal(false);
  hasCarry = signal(false);
  carry = signal(0);

  userAnswer = signal('');
  intermediateAnswer = signal('');

  /** Number of singles from `B` added in the singles carton (beyond the fixed eggs of `A`). */
  single = computed(() => this.singles() - (this.number()[0] % 10));

  /** Running partial sum shown in the tens row. */
  tenthsValue = computed(() => this.number()[0] + this.tenths() * 10);
  /** Running partial sum shown in the singles row. */
  singlesValue = computed(() => this.number()[0] + this.single() + this.tenths() * 10);
  /** Running partial sum shown in the carry row. */
  carryValue = computed(() => this.number()[0] + this.single() + this.carry() + this.tenths() * 10);

  /** Value the learner must type for the currently active step. */
  expectedIntermediate = computed(() => {
    switch (this.state()) {
      case 'TENTH':
        return this.tenthsValue();
      case 'SINGLE':
        return this.singlesValue();
      case 'CARRY':
        return this.carryValue();
      default:
        return 0;
    }
  });

  correctTenths = computed(() => Math.floor(this.number()[1] / 10));
  correctSingles = computed(() => {
    const num = this.number()[0] % 10;
    const onesOfB = this.number()[1] % 10;
    return Math.min(num + onesOfB, 10);
  });
  correctCarry = computed(() => {
    const num = this.number()[0] % 10;
    const onesOfB = this.number()[1] % 10;
    return Math.max(onesOfB - (10 - num), 0);
  });

  stepCartonsCorrect = computed(() => {
    switch (this.state()) {
      case 'TENTH':
        return this.tenths() === this.correctTenths();
      case 'SINGLE':
        return this.singles() === this.correctSingles();
      case 'CARRY':
        return this.carry() === this.correctCarry();
      default:
        return true;
    }
  });

  /** Wrong-answer feedback: retry once, then reveal the answer and move on. */
  attempt = signal(0);
  isWrong = signal(false);
  shake = signal(false);
  isChecking = signal(false);
  revelation = signal<string | undefined>(undefined);
  readonly revealWaiter = new Waiter();

  finished = output<void>();

  async init() {
    this.tenths.set(0);
    this.singles.set(0);
    this.singlesCartonAdded.set(this.number()[0] % 10 !== 0);
    this.hasCarry.set(false);
    this.carry.set(0);
    this.userAnswer.set('');
    this.intermediateAnswer.set('');
    this.attempt.set(0);
    this.isWrong.set(false);
    this.shake.set(false);
    this.isChecking.set(false);

    this.state.set('QUESTION');
    await delay(2500);
    this.state.set('TENTH');
  }

  ngOnInit() {
    this.init().then();
  }

  addCarton() {
    this.tenths.update((c) => c + 1);
  }

  removeCarton() {
    this.tenths.update((c) => c - 1);
  }

  addEmptyCarton() {
    this.singlesCartonAdded.set(true);
  }

  next() {
    this.intermediateAnswer.set('');
    this.attempt.set(0);
    this.isWrong.set(false);

    switch (this.state()) {
      case 'QUESTION':
        this.state.set('TENTH');
        break;
      case 'TENTH':
        this.pronounceStepSum(this.number()[0], this.tenths() * 10, this.tenthsValue());
        this.state.set('SINGLE');
        break;
      case 'SINGLE': {
        this.pronounceStepSum(this.tenthsValue(), this.single(), this.singlesValue());

        const isAnswer = this.tenths() * 10 + this.cartons() * 10 + this.singles() === this.answer();

        if (this.singles() === 10 && !isAnswer) {
          this.hasCarry.set(true);
          this.state.set('CARRY');
        } else {
          this.state.set('WRITE');
        }
        break;
      }
      case 'CARRY':
        this.pronounceStepSum(this.singlesValue(), this.carry(), this.carryValue());
        this.state.set('WRITE');
        break;
      case 'WRITE':
        break;
    }
  }

  private pronounceStepSum(a: number, b: number, c: number) {
    void this.$pronunciationService.tryPronounce(`${a} plus ${b} is ${c}`);
  }

  addNumber(num: number) {
    this.isWrong.set(false);
    if (this.state() === 'WRITE') {
      this.userAnswer.update((a) => a + num.toString());
    } else {
      this.intermediateAnswer.update((a) => a + num.toString());
    }
  }

  backspace() {
    this.isWrong.set(false);
    if (this.state() === 'WRITE') {
      this.userAnswer.update((a) => a.slice(0, -1));
    } else {
      this.intermediateAnswer.update((a) => a.slice(0, -1));
    }
  }

  submit() {
    if (this.isChecking()) return;

    if (this.state() === 'WRITE') {
      void this.checkAnswer();
    } else {
      void this.checkIntermediateAnswer();
    }
  }

  async checkIntermediateAnswer() {
    if (this.expectedIntermediate().toString() === this.intermediateAnswer()) {
      AudioEffectPlayer.play('CORRECT');
      this.next();
      return;
    }

    const giveUp = await this.handleWrong();
    if (!giveUp) return;

    await this.revealAndContinue(() => this.next());
  }

  async checkAnswer() {
    if (this.answer().toString() === this.userAnswer()) {
      AudioEffectPlayer.play('CORRECT');
      this.pronounceStepSum(this.number()[0], this.number()[1], this.answer());
      if (this.numberIndex() === this.numbers().length - 1) {
        this.finished.emit();
        return;
      }
      this.numberIndex.update((c) => c + 1);
      await this.init();
      return;
    }

    const giveUp = await this.handleWrong();
    if (!giveUp) return;

    await this.revealAndContinue(() => {
      if (this.numberIndex() === this.numbers().length - 1) {
        this.finished.emit();
        return;
      }
      this.numberIndex.update((c) => c + 1);
      void this.init();
    });
  }

  /**
   * Plays the incorrect feedback. Returns `true` when the learner has exhausted
   * their single retry (the caller should then reveal the answer and move on).
   */
  private async handleWrong(): Promise<boolean> {
    this.isChecking.set(true);
    this.isWrong.set(true);
    this.shake.set(true);
    AudioEffectPlayer.play('INCORRECT');

    window.setTimeout(() => this.shake.set(false), 600);
    await delay(2500);

    this.isWrong.set(false);

    if (this.attempt() === 0) {
      this.attempt.set(1);
      this.userAnswer.set('');
      this.intermediateAnswer.set('');
      this.isChecking.set(false);
      return false;
    }

    this.isChecking.set(false);
    return true;
  }

  private async revealAndContinue(continueFn: () => void) {
    this.revelation.set(this.state() === 'WRITE' ? this.answer().toString() : this.expectedIntermediate().toString());
    await this.revealWaiter.wait();
    this.revelation.set(undefined);
    continueFn();
  }
}
